/* global Word */
/* global Office */
/* global OfficeExtension */

import log from "../../../utils/logger";
import { match } from "ts-pattern";
import { okAsync, ResultAsync } from "neverthrow";
import { Performance } from "@/utils/performance";
import { last } from "es-toolkit";
import { diff, ChangeType } from "./diff";

type RangeOperationResult = ResultAsync<Word.Range, string>;

export async function applyTextChanges(targetRange: Word.Range, replacementText: string): Promise<void> {
  const setupTimer = Performance.startProcess("apply setup");
  let range = targetRange.getRange(Word.RangeLocation.whole);
  range.load("text");
  await range.context.sync();
  const originalText = range.text;
  log.info("Initial text", "applyTextChanges", range.text);
  log.info("Applying text changes", "applyTextChanges", {
    originalText,
    replacementText,
    originalLength: originalText.length,
    replacementLength: replacementText.length,
  });
  const textChanges = diff(originalText, replacementText);
  log.debug("Computed text changes", "applyTextChanges", {
    changes: textChanges,
    changeCount: textChanges.length,
    types: textChanges.map((c) => c.type),
  });

  const updateRangeToEnd = (range: Word.Range): RangeOperationResult =>
    ResultAsync.fromPromise(
      updateToParagraphEnd(targetRange.paragraphs, range),
      () => "Failed to update range position"
    );

  Performance.endProcess(setupTimer);
  for (const [changeIndex, { type: changeType, text: changeText }] of textChanges.entries()) {
    log.debug("Processing change operation", "applyTextChanges", { changeType, changeText });
    const operationTimer = Performance.startProcess("Change operation");
    const isLastChange = changeIndex === textChanges.length - 1;

    const operationResult = await match(changeType)
      .with(ChangeType.Add, () => insert(range, changeText, isLastChange))
      .with(ChangeType.Remove, () => deleteText(range, changeText))
      .with(ChangeType.Ignore, () => advancePosition(range, changeText))
      .exhaustive()
      .andThen(updateRangeToEnd);

    operationResult.match(
      (updatedRange) => {
        range = updatedRange;
      },
      (error) => {
        log.error("Text operation failed", "applyTextChanges", { error, changeType, changeText });
        throw new Error(`Failed to apply text change: ${error}`);
      }
    );
    Performance.endProcess(operationTimer);
  }
}

async function updateToParagraphEnd(paragraphs: Word.ParagraphCollection, range: Word.Range): Promise<Word.Range> {
  const updateTimer = Performance.startProcess("Update range to paragraph end");
  paragraphs.load("items");
  await paragraphs.context.sync();

  const lastParagraph = last(paragraphs.items);
  const endOfLastParagraph = lastParagraph.getRange(Word.RangeLocation.end);
  const expandedRange = range.expandToOrNullObject(endOfLastParagraph);

  expandedRange.load(["isNullObject"]);
  await expandedRange.context.sync();

  if (expandedRange.isNullObject) {
    log.error("Range expansion resulted in null object", "updateToParagraphEnd");
  }

  Performance.endProcess(updateTimer);
  return expandedRange;
}

function advancePosition(range: Word.Range, textToSkip: string): RangeOperationResult {
  if (!textToSkip.length) return okAsync(range);

  return search(range, textToSkip).map((foundRange) => foundRange.getRange(Word.RangeLocation.end));
}

function deleteText(range: Word.Range, textToDelete: string): RangeOperationResult {
  return search(range, textToDelete).map((foundRange) => {
    const positionAfterDeletion = foundRange.getRange(Word.RangeLocation.end);
    foundRange.delete();
    return positionAfterDeletion;
  });
}

function insert(range: Word.Range, textToInsert: string, addLineBreak: boolean = false): RangeOperationResult {
  const insertedTextRange = range.insertText(textToInsert, Word.InsertLocation.before);

  if (addLineBreak) {
    insertedTextRange.insertBreak(Word.BreakType.line, Word.InsertLocation.after);
  }

  return ResultAsync.fromPromise(
    insertedTextRange.context.sync().then(() => insertedTextRange.getRange(Word.RangeLocation.end)),
    () => "Failed to insert text into document"
  );
}

function search(searchRange: Word.Range, searchText: string): RangeOperationResult {
  const searchTimer = Performance.startProcess("Text search");
  const searchOptions = { ignoreSpace: false, ignorePunct: false };
  const searchResult = searchRange.search(searchText, searchOptions).getFirstOrNullObject();
  searchResult.load(["isNullObject"]);

  return ResultAsync.fromPromise(
    searchResult.context.sync().then(async () => {
      Performance.endProcess(searchTimer);

      if (searchResult.isNullObject) {
        searchRange.load("text");
        await searchRange.context.sync();
        log.error("Text search failed", "searchForText", {
          rangeText: searchRange.text,
          searchText: searchText,
        });
        throw new Error(`Failed to find text: "${searchText}"`);
      }

      return searchResult;
    }),
    () => "Text search operation failed"
  );
}
